/**
 * TanStack Query hooks for loyalty. Reads are tenant-scoped (server-side session);
 * writes invalidate the affected keys so the UI reflects the new balance/ledger.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import {
  accruePoints,
  confirmReservation,
  deactivateCard,
  generateCardPool,
  getBonusAccount,
  getBonusTransactions,
  getCustomerCards,
  getCustomerDiscount,
  getCustomerTier,
  getProgramConfig,
  getReservation,
  importCardPool,
  initializeDefaults,
  issueCard,
  listCardPool,
  listRules,
  listTiers,
  redeemPoints,
  releaseReservation,
  reservePoints,
  transferCards,
  updateProgramConfig,
  upsertRule,
  upsertTier,
} from '../api/loyaltyApi';
import type {
  BonusAccount,
  BonusReservation,
  BonusTransaction,
  CardPoolOperationResult,
  CardPoolPage,
  CardPoolStatus,
  CustomerDiscountResponse,
  GenerateCardPoolRequest,
  InitializeDefaultsResponse,
  IssueCardRequest,
  LoyaltyCard,
  LoyaltyProgramConfigRequest,
  LoyaltyProgramConfigResponse,
  LoyaltyRule,
  LoyaltyTier,
  PointsMutationRequest,
  ReservePointsRequest,
  TransferCardsRequest,
  UpsertRuleRequest,
  UpsertTierRequest,
} from '../model/types';
import { loyaltyKeys } from './queryKeys';

interface EnabledOption {
  enabled?: boolean;
}

// ── Programme administration ──────────────────────────────────────────────────

export function useLoyaltyTiers(options?: EnabledOption): UseQueryResult<LoyaltyTier[], NormalizedApiError> {
  return useQuery<LoyaltyTier[], NormalizedApiError>({
    queryKey: loyaltyKeys.tiers,
    queryFn: listTiers,
    enabled: options?.enabled ?? true,
  });
}

export function useLoyaltyRules(options?: EnabledOption): UseQueryResult<LoyaltyRule[], NormalizedApiError> {
  return useQuery<LoyaltyRule[], NormalizedApiError>({
    queryKey: loyaltyKeys.rules,
    queryFn: listRules,
    enabled: options?.enabled ?? true,
  });
}

export function useUpsertTier() {
  const queryClient = useQueryClient();
  return useMutation<LoyaltyTier, NormalizedApiError, UpsertTierRequest>({
    mutationFn: upsertTier,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: loyaltyKeys.tiers }),
  });
}

export function useUpsertRule() {
  const queryClient = useQueryClient();
  return useMutation<LoyaltyRule, NormalizedApiError, UpsertRuleRequest>({
    mutationFn: upsertRule,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: loyaltyKeys.rules }),
  });
}

export function useInitializeLoyaltyDefaults() {
  const queryClient = useQueryClient();
  return useMutation<InitializeDefaultsResponse, NormalizedApiError, void>({
    mutationFn: initializeDefaults,
    onSuccess: (data) => {
      queryClient.setQueryData(loyaltyKeys.tiers, data.tiers);
      queryClient.setQueryData(loyaltyKeys.rules, data.rules);
    },
  });
}

// ── Per-customer reads ────────────────────────────────────────────────────────

export function useBonusAccount(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<BonusAccount, NormalizedApiError> {
  return useQuery<BonusAccount, NormalizedApiError>({
    queryKey: loyaltyKeys.customerBonus(customerId ?? ''),
    queryFn: () => getBonusAccount(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

export function useBonusTransactions(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<BonusTransaction[], NormalizedApiError> {
  return useQuery<BonusTransaction[], NormalizedApiError>({
    queryKey: loyaltyKeys.customerTransactions(customerId ?? ''),
    queryFn: () => getBonusTransactions(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

export function useCustomerTier(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<LoyaltyTier, NormalizedApiError> {
  return useQuery<LoyaltyTier, NormalizedApiError>({
    queryKey: loyaltyKeys.customerTier(customerId ?? ''),
    queryFn: () => getCustomerTier(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

export function useCustomerCards(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<LoyaltyCard[], NormalizedApiError> {
  return useQuery<LoyaltyCard[], NormalizedApiError>({
    queryKey: loyaltyKeys.customerCards(customerId ?? ''),
    queryFn: () => getCustomerCards(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

// ── Per-customer operations (invalidate the affected account state) ───────────

function useCustomerAccountInvalidation(customerId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerBonus(customerId) });
    void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerTransactions(customerId) });
    void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerTier(customerId) });
  };
}

export function useAccruePoints(customerId: string) {
  const invalidate = useCustomerAccountInvalidation(customerId);
  return useMutation<BonusTransaction, NormalizedApiError, PointsMutationRequest>({
    mutationFn: (body) => accruePoints(customerId, body),
    onSuccess: invalidate,
  });
}

export function useRedeemPoints(customerId: string) {
  const invalidate = useCustomerAccountInvalidation(customerId);
  return useMutation<BonusTransaction, NormalizedApiError, PointsMutationRequest>({
    mutationFn: (body) => redeemPoints(customerId, body),
    onSuccess: invalidate,
  });
}

export function useIssueCard(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation<LoyaltyCard, NormalizedApiError, IssueCardRequest>({
    mutationFn: (body) => issueCard(customerId, body),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerCards(customerId) }),
  });
}

export function useDeactivateCard(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation<LoyaltyCard, NormalizedApiError, string>({
    mutationFn: (cardNumber) => deactivateCard(cardNumber),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerCards(customerId) }),
  });
}

export function useTransferCards(currentCustomerId: string) {
  const queryClient = useQueryClient();
  return useMutation<LoyaltyCard[], NormalizedApiError, TransferCardsRequest>({
    mutationFn: transferCards,
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerCards(currentCustomerId) }),
  });
}

export function useCustomerDiscount(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<CustomerDiscountResponse, NormalizedApiError> {
  return useQuery<CustomerDiscountResponse, NormalizedApiError>({
    queryKey: loyaltyKeys.customerDiscount(customerId ?? ''),
    queryFn: () => getCustomerDiscount(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

// ── Free-card pool ──────────────────────────────────────────────────────────────

export function useCardPool(
  params: { status?: CardPoolStatus; page?: number; size?: number },
  options?: EnabledOption,
): UseQueryResult<CardPoolPage, NormalizedApiError> {
  return useQuery<CardPoolPage, NormalizedApiError>({
    queryKey: loyaltyKeys.cardPool(params),
    queryFn: () => listCardPool(params),
    enabled: options?.enabled ?? true,
  });
}

export function useImportCardPool() {
  const queryClient = useQueryClient();
  return useMutation<CardPoolOperationResult, NormalizedApiError, File>({
    mutationFn: importCardPool,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: loyaltyKeys.cardPoolAll }),
  });
}

export function useGenerateCardPool() {
  const queryClient = useQueryClient();
  return useMutation<CardPoolOperationResult, NormalizedApiError, GenerateCardPoolRequest>({
    mutationFn: generateCardPool,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: loyaltyKeys.cardPoolAll }),
  });
}

// ── Programme model (BONUS / DISCOUNT / NONE) ────────────────────────────────────

export function useProgramConfig(
  options?: EnabledOption,
): UseQueryResult<LoyaltyProgramConfigResponse, NormalizedApiError> {
  return useQuery<LoyaltyProgramConfigResponse, NormalizedApiError>({
    queryKey: loyaltyKeys.programConfig,
    queryFn: getProgramConfig,
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateProgramConfig() {
  const queryClient = useQueryClient();
  return useMutation<LoyaltyProgramConfigResponse, NormalizedApiError, LoyaltyProgramConfigRequest>({
    mutationFn: updateProgramConfig,
    onSuccess: (data) => queryClient.setQueryData(loyaltyKeys.programConfig, data),
  });
}

// ── Bonus reservations (two-phase holds: reserve → confirm / release) ─────────────

export function useReservation(
  referenceId: string | null,
  options?: EnabledOption,
): UseQueryResult<BonusReservation, NormalizedApiError> {
  return useQuery<BonusReservation, NormalizedApiError>({
    queryKey: loyaltyKeys.reservation(referenceId ?? ''),
    queryFn: () => getReservation(referenceId as string),
    enabled: Boolean(referenceId) && (options?.enabled ?? true),
  });
}

export function useReservePoints(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation<BonusReservation, NormalizedApiError, ReservePointsRequest>({
    mutationFn: (body) => reservePoints(customerId, body),
    onSuccess: (data) => {
      queryClient.setQueryData(loyaltyKeys.reservation(data.referenceId), data);
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerBonus(customerId) });
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerTransactions(customerId) });
    },
  });
}

/** Confirm / release share the same cache effects (keyed on the returned reservation). */
function useReservationSettlement(settle: (referenceId: string) => Promise<BonusReservation>) {
  const queryClient = useQueryClient();
  return useMutation<BonusReservation, NormalizedApiError, string>({
    mutationFn: settle,
    onSuccess: (data) => {
      queryClient.setQueryData(loyaltyKeys.reservation(data.referenceId), data);
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerBonus(data.customerId) });
      void queryClient.invalidateQueries({ queryKey: loyaltyKeys.customerTransactions(data.customerId) });
    },
  });
}

export function useConfirmReservation() {
  return useReservationSettlement(confirmReservation);
}

export function useReleaseReservation() {
  return useReservationSettlement(releaseReservation);
}
